import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import { PDKEditIcon } from '@/components/ui/pdk-icons';
import { Separator } from '@/components/ui/separator';
import UsernameEditor from '@/components/user/username-editor';
import { useAuthContext } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import { useUploadAvatar } from '@/hooks/use-upload-avatar';
import { cn } from '@/lib/utils';
import { userStore } from '@/models/stores/index';
import * as Slider from '@radix-ui/react-slider';
import { createFileRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';

export const Route = createFileRoute('/(authenticated)/profile/')({
  component: AccountPageWrapper,
});

const WIDTH_CLASS = 'w-180';

const ProfilePage = observer(() => {
  const { user: authUser } = useAuthContext();

  const user = userStore.getById(authUser?.id as string);

  const [hasUploadedAvatar, setHasUploadedAvatar] = useState(false);

  const { uploadAvatarMutation } = useUploadAvatar({
    onMutate: () => {
      setHasUploadedAvatar(false);
      toast({
        title: 'Uploading avatar...',
        description: 'You must refresh the page to see the full changes!',
      });
    },
    onSuccess: () => {
      setHasUploadedAvatar(true);
    },
  });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAvatarInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setDialogOpen(true);
      setAvatarFile(file);
    }
  };

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const editor = useRef<AvatarEditor>(null);

  const [scale, setScale] = useState(1);

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto p-6 dark:bg-dark h-full items-center">
      <div className={cn(`mb-8 ${WIDTH_CLASS}`)}>
        <h1 className="text-2xl  mb-2">Profile</h1>
      </div>

      <div className={cn(`mb-10 ${WIDTH_CLASS}`)}>
        <div className="border rounded-lg p-4 mb-2">
          <div className="flex items-start">
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-center gap-2 justify-center text-uneditable text-center">
                  <p className="flex items-center justify-center gap-2 text-sm text-center">
                    Profile picture
                  </p>
                </div>
                <div className="relative group flex items-center">
                  {user && (
                    <PaddockAvatar
                      src={
                        // UX NOTE: because the avatar url in S3 is always the same, there's no way to know if the avatar has been uploaded yet
                        // thus we just use the latest avatarFile (with some state tracking)
                        hasUploadedAvatar && avatarFile
                          ? URL.createObjectURL(avatarFile)
                          : (user?.avatar ?? '')
                      }
                      alt={`${user.first_name} ${user.last_name}`}
                      fallback={`${user.first_name.charAt(0)} ${user.last_name.charAt(0)}`}
                      className="h-8 w-8 rounded-lg"
                      avatarClassName="h-8 w-8"
                    />
                  )}
                  <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-50 transition-opacity duration-200 size-8 bg-black rounded-full flex items-center justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="cursor-pointer rounded-full !bg-black/20 !text-white"
                      onClick={() => avatarInputRef.current?.click()}
                    >
                      <PDKEditIcon className="h-4 w-4" />
                      <input
                        ref={avatarInputRef}
                        id="avatar-upload"
                        type="file"
                        className="hidden"
                        onChange={handleAvatarInputChange}
                      />
                    </Button>
                    <Dialog
                      open={dialogOpen}
                      onOpenChange={() => setDialogOpen(false)}
                    >
                      <DialogContent
                        className="max-w-md flex flex-col items-center justify-center px-0"
                        onClickClose={() => setDialogOpen(false)}
                      >
                        <DialogTitle className="text-start w-full px-4">
                          Adjust your avatar
                        </DialogTitle>
                        <Separator
                          orientation="horizontal"
                          className="w-full"
                        />
                        <AvatarEditor
                          image={avatarFile as File}
                          width={200}
                          height={200}
                          border={0}
                          borderRadius={9999}
                          color={[255, 255, 255, 0.6]} // RGBA
                          scale={scale}
                          rotate={0}
                          ref={editor}
                        />
                        <Slider.Root
                          className="relative flex items-center w-1/2"
                          defaultValue={[1]}
                          onValueChange={(num: number[]): void =>
                            setScale(num[0])
                          }
                          max={2}
                          min={0.1}
                          step={0.01}
                        >
                          <Slider.Track className="bg-gray-300 w-full rounded-full h-1.5">
                            <Slider.Range className="absolute bg-primary rounded-full h-full" />
                          </Slider.Track>
                          <Slider.Thumb className="block w-5 h-5 bg-primary border-2 border-white rounded-full shadow-lg hover:border-primary" />
                        </Slider.Root>

                        <Button
                          className="mt-4"
                          variant="outline"
                          onClick={() => {
                            if (!editor.current) {
                              return;
                            }

                            // This returns a HTMLCanvasElement, it can be made into a data URL or a blob,
                            // drawn on another canvas, or added to the DOM.
                            const canvas = editor.current.getImage();

                            canvas.toBlob((blob) => {
                              if (blob) {
                                uploadAvatarMutation.mutate(blob);
                                setDialogOpen(false);
                              }
                            });
                          }}
                        >
                          Save
                        </Button>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>

              <Separator orientation="horizontal" className="w-full my-4" />
              <div className="flex justify-between items-center">
                <div className="flex flex-col gap-0 text-uneditable text-start">
                  <p className="flex gap-2 text-sm text-start">Username</p>
                  <p className="flex items-center justify-center gap-2 text-xs text-start text-muted-foreground">
                    Nickname or first name, however you want to called in
                    Paddock
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2 justify-center text-uneditable text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-center">
                    <UsernameEditor />
                  </div>
                </div>
              </div>

              <Separator orientation="horizontal" className="w-full my-4" />
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-center gap-2 justify-center text-uneditable text-center">
                  <p className="flex items-center justify-center gap-2 text-sm text-center">
                    Email
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2 justify-center text-uneditable text-center">
                  <p className="flex items-center justify-center gap-2 text-sm text-center">
                    {user?.email}
                  </p>
                </div>
              </div>

              <Separator orientation="horizontal" className="w-full my-4" />
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-center gap-2 justify-center text-uneditable text-center">
                  <p className="flex items-center justify-center gap-2 text-sm text-center">
                    Full name
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2 justify-center text-uneditable text-center">
                  <p className="flex items-center justify-center gap-2 text-sm text-center">
                    {user?.first_name} {user?.last_name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function AccountPageWrapper() {
  return <ProfilePage />;
}
